import { Menu } from '@neo4j-ndl/react';
import { CheckIconOutline } from '@neo4j-ndl/react/icons';
import type { Database } from '@nx/state';
import { useActiveDatabase, useConnection, useDatabases } from '@nx/state';
import { isNullish } from '@nx/stdlib';
import cx from 'classnames';
import type { ComponentProps } from 'react';

const DatabaseMenuItem = ({
  db,
  isSelected,
  ...props
}: ComponentProps<typeof Menu.Item> & { db: Database; isSelected: boolean }) => (
  <Menu.Item
    description={db.default ? 'Default database' : undefined}
    {...props}
    className={cx({
      'text-palette-neutral-text-weakest': db.name === 'system' || db.currentStatus !== 'online',
      '!pl-[27px]': !isSelected,
    })}
    icon={isSelected && <CheckIconOutline />}
  />
);

const DatabasesMenuItems = ({ onSelect }: { onSelect: (dbName: string) => void }) => {
  const databases = useDatabases();
  const selectedDatabase = useActiveDatabase();

  if (isNullish(databases) || databases.length === 0) {
    return null;
  }

  const menuItems = databases
    .map((db) => {
      const isSelected = selectedDatabase?.selectedName === db.name || (isNullish(selectedDatabase) && db.default);

      const aliasesMenuItems = db.aliases.map((alias) => {
        const isAliasSelected = selectedDatabase?.selectedName === alias;
        return (
          <DatabaseMenuItem
            key={alias}
            title={alias}
            description={`Alias for “${db.name}”`}
            isSelected={isAliasSelected}
            onClick={() => onSelect(alias)}
            db={db}
          />
        );
      });

      return [
        <DatabaseMenuItem
          key={db.name}
          title={db.name}
          isSelected={isSelected}
          onClick={() => onSelect(db.name)}
          db={db}
        />,
        ...aliasesMenuItems,
      ];
    })
    .flat();

  /* temporary fix until this is resolved: https://trello.com/c/UZSj0ktU/1144-connection-dropdown-renders-db-names-off-screen-when-large-number-of-dbs-configured */
  return <Menu.Items className={'max-h-[90vh] overflow-y-auto'}>{menuItems}</Menu.Items>;
};

interface Props {
  isMenuOpen: boolean;
  onClose: () => void;
  anchorEl?: React.RefObject<HTMLElement | null>;
}

export function DatabaseMenu({ isMenuOpen, anchorEl, onClose }: Props) {
  const connection = useConnection();

  return (
    <Menu anchorRef={anchorEl} isOpen={isMenuOpen} onClose={() => onClose()}>
      <Menu.Items>
        <DatabasesMenuItems
          onSelect={(dbName) => {
            connection.switchDatabase(dbName);
            onClose();
          }}
        />
      </Menu.Items>
    </Menu>
  );
}
